import { useState } from 'react'
import { adminMerchantService, AdminMerchantCreateData } from '@/services/adminMerchantService'
import { whiteLabelService, WhiteLabelConfigCreateData } from '@/services/whiteLabelService'

interface EnhancedMerchantRegistrationData extends AdminMerchantCreateData {
  // WhiteLabel configuration
  whiteLabel?: {
    custom_domain?: string
    logo?: File
    favicon?: File
    primary_color?: string
    secondary_color?: string
    company_display_name?: string
    support_email?: string
    support_phone?: string
    page_title?: string
    meta_description?: string
    enable_custom_checkout?: boolean
    enable_dashboard_access?: boolean
    enable_api_access?: boolean
    enable_branding_css?: boolean
  }
}

interface RegistrationResult {
  merchant: any
  whiteLabelConfig?: any
  success: boolean
  message: string
}

export const useEnhancedMerchantRegistration = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const registerMerchantWithWhiteLabel = async (data: EnhancedMerchantRegistrationData): Promise<RegistrationResult> => {
    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Create the merchant using admin API
      const merchantData: AdminMerchantCreateData = {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        company_name: data.company_name,
        description: data.description,
        website: data.website,
        currency: data.currency,
        country: data.country,
        payout_crypto_enabled: data.payout_crypto_enabled
      }

      console.log('Creating merchant with admin API...', merchantData)
      const createdMerchant = await adminMerchantService.createMerchant(merchantData)
      console.log('Merchant created successfully:', createdMerchant)

      let whiteLabelConfig = null

      // Step 2: Create white-label configuration if provided
      if (data.whiteLabel && Object.keys(data.whiteLabel).length > 0) {
        try {
          console.log('Creating white-label configuration...', data.whiteLabel)
          
          const whiteLabelData: WhiteLabelConfigCreateData = {
            merchant: createdMerchant.id,
            ...data.whiteLabel
          }

          whiteLabelConfig = await whiteLabelService.createConfig(whiteLabelData)
          console.log('White-label configuration created successfully:', whiteLabelConfig)
        } catch (whiteLabelError) {
          console.warn('Failed to create white-label configuration:', whiteLabelError)
          // Don't fail the entire process if white-label creation fails
        }
      }

      return {
        merchant: createdMerchant,
        whiteLabelConfig,
        success: true,
        message: 'Merchant registered successfully with enhanced configuration!'
      }

    } catch (err: any) {
      console.error('Enhanced merchant registration failed:', err)
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.detail || 
                          err?.message || 
                          'Registration failed. Please try again.'
      setError(errorMessage)
      
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    registerMerchantWithWhiteLabel,
    isLoading,
    error,
    clearError: () => setError(null)
  }
}

// Hook for fetching existing merchants with their white-label configs
export const useMerchantWithWhiteLabel = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getMerchantWithWhiteLabel = async (merchantId: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch merchant details and white-label config in parallel
      const [merchant, whiteLabelConfig] = await Promise.all([
        adminMerchantService.getMerchantById(merchantId),
        whiteLabelService.getConfigByMerchant(merchantId)
      ])

      return {
        merchant,
        whiteLabelConfig,
        success: true
      }

    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.detail || 
                          err?.message || 
                          'Failed to fetch merchant data.'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const updateMerchantWithWhiteLabel = async (
    merchantId: string, 
    merchantData?: any, 
    whiteLabelData?: any
  ) => {
    setIsLoading(true)
    setError(null)

    try {
      const promises = []

      // Update merchant if data provided
      if (merchantData) {
        promises.push(adminMerchantService.updateMerchant(merchantId, merchantData))
      }

      // Update or create white-label config if data provided
      if (whiteLabelData) {
        const existingConfig = await whiteLabelService.getConfigByMerchant(merchantId)
        
        if (existingConfig) {
          promises.push(whiteLabelService.updateConfig(existingConfig.id, whiteLabelData))
        } else {
          promises.push(whiteLabelService.createConfig({
            merchant: merchantId,
            ...whiteLabelData
          }))
        }
      }

      const results = await Promise.all(promises)

      return {
        merchant: merchantData ? results[0] : null,
        whiteLabelConfig: whiteLabelData ? results[merchantData ? 1 : 0] : null,
        success: true,
        message: 'Merchant updated successfully!'
      }

    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.detail || 
                          err?.message || 
                          'Failed to update merchant.'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getMerchantWithWhiteLabel,
    updateMerchantWithWhiteLabel,
    isLoading,
    error,
    clearError: () => setError(null)
  }
}